import { useState, useEffect, useCallback, useRef } from 'react';
import { getMyAdminOrganizationsUsersAPI, getAdminOrganizationsPaginationAPI } from '../../Services/api';
import { get } from 'lodash';
import { SIGNDOC_ASSIGNMENT_PAGINATION } from '../SignDoc/SignDocLib';

/**
 * Data model types supported by this hook.
 * @typedef {'users' | 'organizations'} DataModelType
 */

/**
 * Custom hook for fetching paginated admin data (users or organizations) with search support.
 * 
 * This unified hook manages pagination state and provides methods to navigate between pages,
 * refetch data, and reset the pagination state. Supports searching by relevant fields.
 * 
 * @param {object} params - Hook parameters
 * @param {DataModelType} params.dataModelType - Type of data model to fetch: 'users' or 'organizations'
 * @param {boolean} params.enabled - Whether to fetch data (e.g., when modal is open and conditions met)
 * @param {number} [params.pageSize=30] - Number of items per page (default: 30 from SIGNDOC_ASSIGNMENT_PAGINATION)
 * @param {string} [params.searchQuery=''] - Search query to filter items (debounced value recommended)
 * @returns {object} Hook return value
 * @returns {Array} returns.data - Current page of items (users or organizations)
 * @returns {number} returns.totalCount - Total count of items from API
 * @returns {number} returns.currentPage - Current page number (0-indexed)
 * @returns {boolean} returns.isLoading - Loading state
 * @returns {Error|null} returns.error - Error object if fetch failed, null otherwise
 * @returns {Function} returns.setPage - Function to change page: (pageNumber) => void
 * @returns {Function} returns.refetch - Function to refetch current page: () => void
 * @returns {Function} returns.reset - Function to reset pagination state: () => void
 * 
 * @example
 * // Simple usage - pageSize defaults to 30 (SIGNDOC_ASSIGNMENT_PAGINATION)
 * const { data, totalCount, currentPage, isLoading, setPage } = useAdminDataPagination({
 *   dataModelType: 'users', // or 'organizations'
 *   enabled: modalOpen,
 *   searchQuery: debouncedSearchValue
 * });
 */
export const useAdminDataPagination = ({ dataModelType, enabled, pageSize = SIGNDOC_ASSIGNMENT_PAGINATION.USERS_PAGE_SIZE, searchQuery = '' }) => {
    const [data, setData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Track previous search query to detect changes and reset page
    const prevSearchQueryRef = useRef(searchQuery);

    /**
     * Fetches data for a specific page with optional search filtering.
     * Handles both users and organizations based on the dataModelType parameter.
     * 
     * @param {object} params - Fetch parameters
     * @param {number} params.page - Page number to fetch (0-indexed)
     * @returns {Promise<void>}
     */
    const fetchData = useCallback(async ({ page }) => {
        if (!enabled) return;

        setIsLoading(true);
        setError(null);

        try {
            let response;
            let fetchedData;
            let count;

            const filterModel = {
                filterData: {},
                filterValues: {
                    search: searchQuery || ''
                }
            };
            const sortModel = [];
            const paginationModel = {
                page,
                pageSize
            };


            if (dataModelType === 'users') {
                response = await getMyAdminOrganizationsUsersAPI({filterModel, sortModel, paginationModel});
                fetchedData = get(response, 'users', []);
                count = get(response, 'count', 0);
            } else {
                // dataModelType === 'organizations'
                response = await getAdminOrganizationsPaginationAPI({
                    filterModel,
                    sortModel,
                    paginationModel,
                    withAdminPermission: true
                });
                fetchedData = get(response, 'data', []);
                count = get(response, 'count', 0);
            }

            setData(fetchedData);
            setTotalCount(count);
            setCurrentPage(page);
        } catch (err) {
            console.error(`Error fetching ${dataModelType}:`, err);
            setError(err);
            setData([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    }, [enabled, pageSize, searchQuery, dataModelType]);

    /**
     * Unified effect to handle all data fetching and state management.
     * This single effect prevents race conditions by ensuring state changes
     * are processed in the correct order before fetching data.
     * 
     * Flow:
     * 1. If disabled -> reset all state and exit
     * 2. If search changed -> reset page to 0 (if needed) and fetch with new search
     * 3. Otherwise -> fetch data for current page
     */
    useEffect(() => {
        // Handle disabled state first - reset everything
        if (!enabled) {
            setData([]);
            setTotalCount(0);
            setCurrentPage(0);
            setError(null);
            prevSearchQueryRef.current = searchQuery;
            return;
        }

        // Check if search changed - reset page to 0 and fetch with new search
        if (prevSearchQueryRef.current !== searchQuery) {
            prevSearchQueryRef.current = searchQuery;
            // If we're not on page 0, set page to 0 (will trigger re-fetch via effect)
            if (currentPage !== 0) {
                setCurrentPage(0);
                return;
            }
            // If we're already on page 0, fetch immediately with new search
            fetchData({ page: 0 });
            return;
        }

        // Normal fetch for current page
        fetchData({ page: currentPage });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, currentPage, pageSize, searchQuery, dataModelType]);

    /**
     * Sets the current page number.
     * Triggers a fetch of the new page via useEffect.
     * 
     * @param {number} newPage - New page number (0-indexed)
     * @returns {void}
     */
    const setPage = useCallback((newPage) => {
        if (newPage >= 0) {
            setCurrentPage(newPage);
        }
    }, []);

    /**
     * Refetches the current page.
     * Useful for refreshing data after external changes.
     * 
     * @returns {Promise<void>}
     */
    const refetch = useCallback(() => {
        return fetchData({ page: currentPage });
    }, [currentPage, fetchData]);

    /**
     * Resets pagination state to initial values.
     * Clears data, resets page to 0, and clears errors.
     * 
     * @returns {void}
     */
    const reset = useCallback(() => {
        setCurrentPage(0);
        setData([]);
        setTotalCount(0);
        setError(null);
    }, []);

    return {
        data,
        totalCount,
        currentPage,
        isLoading,
        error,
        setPage,
        refetch,
        reset
    };
};

export default useAdminDataPagination;

